<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useQuasar } from 'quasar';
import DOMPurify from 'dompurify';

import { Constants } from '@/constants';
import { getAccountTypeOptions, isCustomerAccountType } from '@/helpers/accountType.helper';
import { formatDate } from '@/plugins/i18n';
import { useCommunityStore } from '@/stores/community.store';
import {
  useCustomersOverviewStore,
  type CustomerOverviewRow,
  type StageCounts,
  type DateRange,
} from '@/stores/customersOverview.store';

const { t, locale } = useI18n();
const $q = useQuasar();
const communityStore = useCommunityStore();
const customersStore = useCustomersOverviewStore();

// Only finance may confirm (green) and edit confirmed dates. The backend is the sole
// authority (config.finance.confirmerEmail) and tells us, per request, whether the current
// user qualifies — the confirmer's address itself never reaches the client.
const isFinance = computed(() => customersStore.canConfirmContract);

const CONFIDENCE_COLOR: Record<string, string> = {
  unverified: 'grey-5',
  hubspot_matched: 'orange',
  confirmed: 'positive',
};

const customers = computed(() => customersStore.customers);
const loading = computed(() => customersStore.isLoading);
const filter = ref('');
const selectedFeatures = ref<string[] | null>([]);
const selectedStatuses = ref<string[] | null>([]);

// --- Date range selector -------------------------------------------------
const datePreset = ref<'last7' | 'last30' | 'last90' | 'ytd' | 'all' | 'custom'>('last30');
const customFrom = ref('');
const customTo = ref('');

const presetOptions = computed(() => [
  { label: t('customerOverview.period.last7'), value: 'last7' },
  { label: t('customerOverview.period.last30'), value: 'last30' },
  { label: t('customerOverview.period.last90'), value: 'last90' },
  { label: t('customerOverview.period.ytd'), value: 'ytd' },
  { label: t('customerOverview.period.all'), value: 'all' },
  { label: t('customerOverview.period.custom'), value: 'custom' },
]);

function ymd(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function computeRange(preset: string): DateRange {
  const to = new Date();
  let from = new Date();
  switch (preset) {
    case 'last7':
      from.setDate(from.getDate() - 7);
      break;
    case 'last90':
      from.setDate(from.getDate() - 90);
      break;
    case 'ytd':
      from = new Date(to.getFullYear(), 0, 1);
      break;
    case 'all':
      from = new Date(2000, 0, 1);
      break;
    case 'last30':
    default:
      from.setDate(from.getDate() - 30);
      break;
  }
  return { from: ymd(from), to: ymd(to) };
}

async function reload() {
  let range: DateRange;
  if (datePreset.value === 'custom') {
    if (!customFrom.value || !customTo.value) return; // wait for both
    range = { from: customFrom.value, to: customTo.value };
  } else {
    range = computeRange(datePreset.value);
  }
  await customersStore.fetchCustomersOverview(range);
}

watch(datePreset, () => {
  if (datePreset.value !== 'custom') reload();
});
watch([customFrom, customTo], () => {
  if (datePreset.value === 'custom') reload();
});

// --- Display helpers -----------------------------------------------------
const pagination = ref({
  sortBy: 'customer',
  descending: false,
  page: 1,
  rowsPerPage: 0, // 0 = show all (master view; ~50 rows)
});

function localized(value: { en?: string | null; fr?: string | null } | null): string {
  if (!value) return '';
  const lang = locale.value?.startsWith('en') ? 'en' : 'fr';
  return value[lang] || value.fr || value.en || '';
}

function displayName(row: CustomerOverviewRow): string {
  return localized(row.organizationName) || localized(row.title) || row.slug;
}

function stageTotal(s: StageCounts): number {
  return s.prelaunch + s.ongoing + s.completed + s.archived;
}
function stageCompact(s: StageCounts): string {
  return `${s.prelaunch}·${s.ongoing}·${s.completed}·${s.archived}`;
}

// Flatten the community `options` JSON into the list of enabled feature names.
function enabledFeatures(options: Record<string, any> | null): string[] {
  if (!options) return [];
  const out: string[] = [];
  const walk = (obj: Record<string, any>, prefix: string) => {
    for (const [key, value] of Object.entries(obj)) {
      const name = key === 'enabled' ? prefix : prefix ? `${prefix}.${key}` : key;
      if (value === true && name) {
        out.push(name);
      } else if (value && typeof value === 'object') {
        walk(value, prefix ? `${prefix}.${key}` : key);
      }
    }
  };
  walk(options, '');
  return [...new Set(out)];
}

function featureLabel(name: string): string {
  return name.split('.').pop()!.replace(/_/g, ' ');
}

// enabledFeatures walks the options JSON, so compute it once per row per data load
// and read the cached list everywhere (filter, column, and cell template) instead of
// re-walking on every render.
const featuresByRow = computed(() => new Map(customers.value.map((c) => [c.id, enabledFeatures(c.options)])));
function rowFeatures(row: CustomerOverviewRow): string[] {
  return featuresByRow.value.get(row.id) ?? [];
}

// Distinct list of every feature enabled by at least one customer, for the filter.
const availableFeatures = computed(() => {
  const set = new Set<string>();
  for (const features of featuresByRow.value.values()) {
    for (const f of features) set.add(f);
  }
  return [...set].sort().map((f) => ({ label: featureLabel(f), value: f }));
});

// Status filter options (effective statuses, in display order).
const statusFilterOptions = computed(() =>
  ['active', 'churned', 'unknown', 'demo', 'internal', 'past'].map((k) => ({
    label: t(`customerOverview.status.${k}`),
    value: k,
  })),
);

// Account-type choices for the inline status editor.
const accountTypeOptions = computed(() => getAccountTypeOptions());

// Rows after applying the status filter and the feature filter (a customer must
// have ALL selected features enabled). The q-table's text search runs on top.
// Guard against null: Quasar's clearable emits null (not []) when cleared.
const filteredRows = computed(() => {
  const statuses = selectedStatuses.value ?? [];
  const features = selectedFeatures.value ?? [];
  let rows = customers.value;
  if (statuses.length) {
    rows = rows.filter((c) => statuses.includes(effectiveStatus(c)));
  }
  if (features.length) {
    rows = rows.filter((c) => {
      const enabled = rowFeatures(c);
      return features.every((f) => enabled.includes(f));
    });
  }
  return rows;
});

function hasCustomDomain(row: CustomerOverviewRow): boolean {
  return !!(row.customUrl && row.customUrl.enabled && row.customUrl.hostname);
}

const AT = Constants.Community.AccountType;

const STATUS_COLORS: Record<string, string> = {
  active: 'positive',
  churned: 'negative',
  unknown: 'grey',
  demo: 'blue',
  internal: 'deep-purple',
  past: 'blue-grey',
};

// The single status key shown as the badge and used by the status filter.
// Non-customer account types override the contract-derived status.
function effectiveStatus(row: CustomerOverviewRow): string {
  if (row.accountType === AT.Demo) return 'demo';
  if (row.accountType === AT.Internal) return 'internal';
  if (row.accountType === AT.PastCustomer) return 'past';
  return row.commercialStatus; // 'active' | 'churned' | 'unknown'
}

function statusMeta(row: CustomerOverviewRow): { key: string; label: string; color: string } {
  const key = effectiveStatus(row);
  return { key, label: t(`customerOverview.status.${key}`), color: STATUS_COLORS[key] ?? 'grey' };
}

// Contract dates are DATEONLY 'YYYY-MM-DD' strings; render as-is (no timezone math).
// Timestamps like lastActivityAt use the shared locale-aware formatDate from @/plugins/i18n.
function formatDateOnly(value: string | null): string {
  return value || '—';
}

const columns = computed(() => [
  {
    name: 'customer',
    label: t('customerOverview.columns.customer'),
    field: (row: CustomerOverviewRow) => displayName(row),
    align: 'left' as const,
    sortable: true,
  },
  {
    name: 'status',
    label: t('customerOverview.columns.status'),
    field: (row: CustomerOverviewRow) => statusMeta(row).label,
    align: 'left' as const,
    sortable: true,
  },
  {
    name: 'admins',
    label: t('customerOverview.columns.admins'),
    field: 'adminCount',
    align: 'center' as const,
    sortable: true,
  },
  {
    name: 'editors',
    label: t('customerOverview.columns.editors'),
    field: 'editorCount',
    align: 'center' as const,
    sortable: true,
  },
  {
    name: 'consultations',
    label: t('customerOverview.columns.consultations'),
    field: (row: CustomerOverviewRow) => stageTotal(row.consultationsByStage),
    align: 'center' as const,
    sortable: true,
  },
  {
    name: 'consultationsActivity',
    label: t('customerOverview.columns.launched'),
    field: (row: CustomerOverviewRow) => row.consultationsLaunchedInPeriod,
    align: 'center' as const,
    sortable: true,
  },
  {
    name: 'projects',
    label: t('customerOverview.columns.projects'),
    field: (row: CustomerOverviewRow) => stageTotal(row.projectsByStage),
    align: 'center' as const,
    sortable: true,
  },
  {
    name: 'projectsActivity',
    label: t('customerOverview.columns.launched'),
    field: (row: CustomerOverviewRow) => row.projectsLaunchedInPeriod,
    align: 'center' as const,
    sortable: true,
  },
  {
    name: 'features',
    label: t('customerOverview.columns.features'),
    field: (row: CustomerOverviewRow) => rowFeatures(row).length,
    align: 'center' as const,
    sortable: true,
  },
  {
    name: 'customDomain',
    label: t('customerOverview.columns.customDomain'),
    field: (row: CustomerOverviewRow) => (hasCustomDomain(row) ? 1 : 0),
    align: 'left' as const,
    sortable: true,
  },
  {
    name: 'contractStart',
    label: t('customerOverview.columns.contractStart'),
    field: (row: CustomerOverviewRow) => row.contractStartDate ?? '',
    align: 'left' as const,
    sortable: true,
  },
  {
    name: 'renewal',
    label: t('customerOverview.columns.renewal'),
    field: (row: CustomerOverviewRow) => row.contractEndDate ?? '',
    align: 'left' as const,
    sortable: true,
  },
  {
    name: 'lastActivity',
    label: t('customerOverview.columns.lastActivity'),
    field: (row: CustomerOverviewRow) => row.lastActivityAt ?? '',
    align: 'left' as const,
    sortable: true,
  },
  {
    name: 'hubspot',
    label: t('customerOverview.columns.hubspot'),
    field: 'hubspotCompanyId',
    align: 'left' as const,
    sortable: false,
  },
]);

async function handleRowClick(_evt: Event, row: CustomerOverviewRow) {
  await communityStore.switchCommunity({ id: row.id } as any, true);
}

// Persist an inline edit of the commercial fields. Sends the full current triple with
// the edited field applied; the store updates the row (incl. recomputed status) on success.
async function saveCommercial(
  row: CustomerOverviewRow,
  patch: Partial<{
    accountType: number;
    hubspotCompanyId: string | null;
    contractStartDate: string | null;
    contractEndDate: string | null;
  }>,
) {
  const merged = {
    accountType: row.accountType,
    hubspotCompanyId: row.hubspotCompanyId,
    contractStartDate: row.contractStartDate,
    contractEndDate: row.contractEndDate,
    ...patch,
  };
  const clean = {
    accountType: merged.accountType,
    hubspotCompanyId: merged.hubspotCompanyId ? String(merged.hubspotCompanyId).trim() || null : null,
    contractStartDate: merged.contractStartDate || null,
    contractEndDate: merged.contractEndDate || null,
  };
  try {
    await customersStore.updateCommercial(row.id, clean);
  } catch (e: any) {
    $q.notify({ type: 'negative', message: e?.response?.data?.message ?? t('customerOverview.saveError') });
  }
}

// Moving a community out of the customer types lets a superadmin in without
// the customer's request (COCO-3425), so it is confirmed first. The API audits
// every change of type.
function saveAccountType(row: CustomerOverviewRow, accountType: number) {
  if (accountType === row.accountType) return;
  if (!isCustomerAccountType(row.accountType) || isCustomerAccountType(accountType)) {
    saveCommercial(row, { accountType });
    return;
  }
  $q.dialog({
    title: t('customerAccess.leaveCustomer.title'),
    message: t('customerAccess.leaveCustomer.message', {
      community: displayName(row),
      type: getAccountTypeOptions().find((o) => o.value === accountType)?.label ?? accountType,
    }),
    cancel: { label: t('global.cancel'), flat: true },
    ok: { label: t('customerAccess.leaveCustomer.ok'), color: 'negative' },
    persistent: true,
  }).onOk(() => saveCommercial(row, { accountType }));
}

// True when the current user may edit this row's contract dates.
function canEditDates(row: CustomerOverviewRow): boolean {
  return row.contractConfidence !== 'confirmed' || isFinance.value;
}

// Pull the latest closed-won contract from HubSpot, then review + apply the dates.
async function pullFromHubspot(row: CustomerOverviewRow) {
  try {
    const proposal = await customersStore.pullHubspotContract(row.id);
    if (!proposal.found) {
      $q.notify({ type: 'warning', message: t('customerOverview.hubspot.noDeal') });
      await reload();
      return;
    }
    $q.dialog({
      title: t('customerOverview.hubspot.pullTitle'),
      // html: true is needed for the <b>/<br> layout, and the deal name is third-party data
      // coming straight back from HubSpot — sanitize down to just those two tags.
      message: DOMPurify.sanitize(
        `<b>${proposal.dealName ?? ''}</b><br>` +
          `${t('customerOverview.columns.contractStart')}: ${proposal.contractStartDate ?? '—'}<br>` +
          `${t('customerOverview.columns.renewal')}: ${proposal.contractEndDate ?? '—'}`,
        { ALLOWED_TAGS: ['b', 'br'], ALLOWED_ATTR: [] },
      ),
      html: true,
      cancel: { label: t('global.cancel'), flat: true },
      ok: { label: t('customerOverview.hubspot.apply'), color: 'primary' },
    }).onOk(async () => {
      // saveCommercial's PATCH response returns the recomputed confidence fields and the
      // store patches them onto the row, so the dot turns orange without a full reload.
      await saveCommercial(row, {
        contractStartDate: proposal.contractStartDate ?? null,
        contractEndDate: proposal.contractEndDate ?? null,
      });
    });
  } catch (e: any) {
    $q.notify({ type: 'negative', message: e?.response?.data?.message ?? t('customerOverview.hubspot.pullError') });
  }
}

// Finance-only: confirm (green/lock) or un-confirm the dates.
async function toggleConfirm(row: CustomerOverviewRow) {
  const confirmed = row.contractConfidence !== 'confirmed';
  try {
    await customersStore.confirmContract(row.id, confirmed);
  } catch (e: any) {
    $q.notify({ type: 'negative', message: e?.response?.data?.message ?? t('customerOverview.saveError') });
  }
}

onMounted(async () => {
  await reload();
});
</script>

<template>
  <q-page class="customer-overview">
    <div class="header-row">
      <h1 class="title">{{ t('customerOverview.title') }}</h1>
      <div class="period-controls">
        <q-select
          v-model="selectedStatuses"
          :options="statusFilterOptions"
          emit-value
          map-options
          multiple
          use-chips
          clearable
          dense
          outlined
          options-dense
          :label="t('customerOverview.statusFilter')"
          style="min-width: 200px; max-width: 340px"
        />
        <q-select
          v-model="selectedFeatures"
          :options="availableFeatures"
          emit-value
          map-options
          multiple
          use-chips
          clearable
          dense
          outlined
          options-dense
          :label="t('customerOverview.featureFilter')"
          style="min-width: 220px; max-width: 380px"
        />
        <q-select
          v-model="datePreset"
          :options="presetOptions"
          emit-value
          map-options
          dense
          outlined
          options-dense
          :label="t('customerOverview.period.label')"
          style="min-width: 170px"
        />
        <template v-if="datePreset === 'custom'">
          <q-input v-model="customFrom" type="date" dense outlined :label="t('customerOverview.period.from')" />
          <q-input v-model="customTo" type="date" dense outlined :label="t('customerOverview.period.to')" />
        </template>
      </div>
    </div>

    <q-table
      :rows="filteredRows"
      :columns="columns as any"
      row-key="id"
      :loading="loading"
      :filter="filter"
      v-model:pagination="pagination"
      :rows-per-page-options="[0, 25, 50, 100]"
      flat
      bordered
      wrap-cells
      @row-click="handleRowClick"
    >
      <template v-slot:top-right>
        <q-input v-model="filter" dense debounce="200" :placeholder="t('customerOverview.search')" clearable>
          <template v-slot:prepend>
            <q-icon name="search" />
          </template>
        </q-input>
      </template>

      <!-- Customer: name + slug preview link -->
      <template v-slot:body-cell-customer="props">
        <q-td :props="props">
          <div class="customer-cell">
            <div class="customer-name">{{ displayName(props.row) }}</div>
            <a :href="communityStore.previewURLForSlug(props.row.slug)" target="_blank" class="slug-link" @click.stop>
              {{ props.row.slug }}
              <q-icon name="open_in_new" size="12px" />
            </a>
          </div>
        </q-td>
      </template>

      <!-- Status badge (account type editable inline) -->
      <template v-slot:body-cell-status="props">
        <q-td :props="props" class="editable" @click.stop>
          <q-badge :color="statusMeta(props.row).color" :label="statusMeta(props.row).label" />
          <q-icon name="edit" size="12px" class="edit-hint" />
          <q-popup-edit
            :model-value="props.row.accountType"
            v-slot="scope"
            buttons
            :label-set="t('global.save')"
            :label-cancel="t('global.cancel')"
            @save="(val: number) => saveAccountType(props.row, val)"
          >
            <q-select
              v-model="scope.value"
              :options="accountTypeOptions"
              emit-value
              map-options
              dense
              autofocus
              :label="t('customerOverview.accountType.label')"
            />
          </q-popup-edit>
        </q-td>
      </template>

      <!-- Consultations: compact stage counts + tooltip -->
      <template v-slot:body-cell-consultations="props">
        <q-td :props="props">
          <span class="stage-compact">{{ stageCompact(props.row.consultationsByStage) }}</span>
          <q-tooltip>
            <div>{{ t('customerOverview.stages.prelaunch') }}: {{ props.row.consultationsByStage.prelaunch }}</div>
            <div>{{ t('customerOverview.stages.ongoing') }}: {{ props.row.consultationsByStage.ongoing }}</div>
            <div>{{ t('customerOverview.stages.completed') }}: {{ props.row.consultationsByStage.completed }}</div>
            <div>{{ t('customerOverview.stages.archived') }}: {{ props.row.consultationsByStage.archived }}</div>
          </q-tooltip>
        </q-td>
      </template>

      <!-- Projects: compact stage counts + tooltip -->
      <template v-slot:body-cell-projects="props">
        <q-td :props="props">
          <span class="stage-compact">{{ stageCompact(props.row.projectsByStage) }}</span>
          <q-tooltip>
            <div>{{ t('customerOverview.stages.prelaunch') }}: {{ props.row.projectsByStage.prelaunch }}</div>
            <div>{{ t('customerOverview.stages.ongoing') }}: {{ props.row.projectsByStage.ongoing }}</div>
            <div>{{ t('customerOverview.stages.completed') }}: {{ props.row.projectsByStage.completed }}</div>
            <div>{{ t('customerOverview.stages.archived') }}: {{ props.row.projectsByStage.archived }}</div>
          </q-tooltip>
        </q-td>
      </template>

      <!-- Consultations: launched / created in the selected period -->
      <template v-slot:body-cell-consultationsActivity="props">
        <q-td :props="props">
          <span class="stage-compact">
            {{ props.row.consultationsLaunchedInPeriod }}&nbsp;/&nbsp;{{ props.row.consultationsCreatedInPeriod }}
          </span>
          <q-tooltip>
            <div>{{ t('customerOverview.columns.launched') }}: {{ props.row.consultationsLaunchedInPeriod }}</div>
            <div>{{ t('customerOverview.columns.created') }}: {{ props.row.consultationsCreatedInPeriod }}</div>
          </q-tooltip>
        </q-td>
      </template>

      <!-- Projects: launched / created in the selected period -->
      <template v-slot:body-cell-projectsActivity="props">
        <q-td :props="props">
          <span class="stage-compact">
            {{ props.row.projectsLaunchedInPeriod }}&nbsp;/&nbsp;{{ props.row.projectsCreatedInPeriod }}
          </span>
          <q-tooltip>
            <div>{{ t('customerOverview.columns.launched') }}: {{ props.row.projectsLaunchedInPeriod }}</div>
            <div>{{ t('customerOverview.columns.created') }}: {{ props.row.projectsCreatedInPeriod }}</div>
          </q-tooltip>
        </q-td>
      </template>

      <!-- Features: count + hover tooltip listing them -->
      <template v-slot:body-cell-features="props">
        <q-td :props="props">
          <template v-if="rowFeatures(props.row).length">
            <q-badge outline color="primary" class="cursor-pointer">
              {{ t('customerOverview.featuresEnabled', { count: rowFeatures(props.row).length }) }}
              <q-tooltip class="feature-tooltip">
                <div v-for="f in rowFeatures(props.row)" :key="f">{{ featureLabel(f) }}</div>
              </q-tooltip>
            </q-badge>
          </template>
          <span v-else class="muted">—</span>
        </q-td>
      </template>

      <!-- Custom domain -->
      <template v-slot:body-cell-customDomain="props">
        <q-td :props="props">
          <template v-if="hasCustomDomain(props.row)">
            <a :href="`https://${props.row.customUrl.hostname.trim()}`" target="_blank" class="slug-link" @click.stop>
              <q-icon name="link" size="14px" /> {{ props.row.customUrl.hostname.trim() }}
            </a>
          </template>
          <span v-else class="muted">—</span>
        </q-td>
      </template>

      <!-- Contract start (editable + confidence dot) -->
      <template v-slot:body-cell-contractStart="props">
        <q-td :props="props" :class="canEditDates(props.row) ? 'editable' : ''" @click.stop>
          <q-icon
            name="fiber_manual_record"
            size="10px"
            :color="CONFIDENCE_COLOR[props.row.contractConfidence]"
            class="conf-dot"
          >
            <q-tooltip>{{ t('customerOverview.confidence.' + props.row.contractConfidence) }}</q-tooltip>
          </q-icon>
          {{ formatDateOnly(props.row.contractStartDate) }}
          <template v-if="canEditDates(props.row)">
            <q-icon name="edit" size="12px" class="edit-hint" />
            <q-popup-edit
              :model-value="props.row.contractStartDate"
              v-slot="scope"
              buttons
              :label-set="t('global.save')"
              :label-cancel="t('global.cancel')"
              @save="(val: string) => saveCommercial(props.row, { contractStartDate: val || null })"
            >
              <q-input
                v-model="scope.value"
                type="date"
                dense
                autofocus
                :label="t('customerOverview.columns.contractStart')"
              />
            </q-popup-edit>
          </template>
        </q-td>
      </template>

      <!-- Contract end (editable + confidence dot + divergence + finance confirm) -->
      <template v-slot:body-cell-renewal="props">
        <q-td :props="props" :class="canEditDates(props.row) ? 'editable' : ''" @click.stop>
          <q-icon
            name="fiber_manual_record"
            size="10px"
            :color="CONFIDENCE_COLOR[props.row.contractConfidence]"
            class="conf-dot"
          >
            <q-tooltip>
              {{ t('customerOverview.confidence.' + props.row.contractConfidence)
              }}<template v-if="props.row.contractConfirmedBy"> — {{ props.row.contractConfirmedBy }}</template>
            </q-tooltip>
          </q-icon>
          {{ formatDateOnly(props.row.contractEndDate) }}
          <q-icon v-if="props.row.hsDiverged" name="warning" size="14px" color="orange" class="q-ml-xs">
            <q-tooltip>{{ t('customerOverview.confidence.diverged') }}</q-tooltip>
          </q-icon>
          <template v-if="canEditDates(props.row)">
            <q-icon name="edit" size="12px" class="edit-hint" />
            <q-popup-edit
              :model-value="props.row.contractEndDate"
              v-slot="scope"
              buttons
              :label-set="t('global.save')"
              :label-cancel="t('global.cancel')"
              @save="(val: string) => saveCommercial(props.row, { contractEndDate: val || null })"
            >
              <q-input
                v-model="scope.value"
                type="date"
                dense
                autofocus
                :label="t('customerOverview.columns.renewal')"
              />
            </q-popup-edit>
          </template>
          <q-btn
            v-if="isFinance"
            flat
            dense
            round
            size="sm"
            :icon="props.row.contractConfidence === 'confirmed' ? 'verified' : 'check_circle_outline'"
            :color="props.row.contractConfidence === 'confirmed' ? 'positive' : 'grey'"
            @click.stop="toggleConfirm(props.row)"
          >
            <q-tooltip>
              {{
                props.row.contractConfidence === 'confirmed'
                  ? t('customerOverview.confidence.unconfirm')
                  : t('customerOverview.confidence.confirm')
              }}
            </q-tooltip>
          </q-btn>
        </q-td>
      </template>

      <!-- Last activity (timestamp: locale-aware, local timezone) -->
      <template v-slot:body-cell-lastActivity="props">
        <q-td :props="props">{{ formatDate(props.row.lastActivityAt) || '—' }}</q-td>
      </template>

      <!-- HubSpot company id (editable) -->
      <template v-slot:body-cell-hubspot="props">
        <q-td :props="props" class="editable" @click.stop>
          <span v-if="props.row.hubspotCompanyId" class="mono">{{ props.row.hubspotCompanyId }}</span>
          <span v-else class="muted">—</span>
          <q-btn
            v-if="props.row.hubspotCompanyId"
            flat
            dense
            round
            size="sm"
            icon="cloud_download"
            color="primary"
            @click.stop="pullFromHubspot(props.row)"
          >
            <q-tooltip>{{ t('customerOverview.hubspot.pull') }}</q-tooltip>
          </q-btn>
          <q-icon name="edit" size="12px" class="edit-hint" />
          <q-popup-edit
            :model-value="props.row.hubspotCompanyId"
            v-slot="scope"
            buttons
            :label-set="t('global.save')"
            :label-cancel="t('global.cancel')"
            @save="(val: string) => saveCommercial(props.row, { hubspotCompanyId: val })"
          >
            <q-input
              :model-value="scope.value"
              @update:model-value="(v: string | number | null) => (scope.value = String(v ?? '').replace(/\D/g, ''))"
              dense
              autofocus
              inputmode="numeric"
              maxlength="50"
              :label="t('customerOverview.columns.hubspot')"
              @keyup.enter="scope.set"
            />
          </q-popup-edit>
        </q-td>
      </template>
    </q-table>
  </q-page>
</template>

<style lang="scss">
.customer-overview {
  padding: 16px;

  .header-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 12px;
  }
  .title {
    margin: 0 10px !important;
  }
  .period-controls {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  .customer-cell {
    display: flex;
    flex-direction: column;
    line-height: 1.3;
  }
  .customer-name {
    font-weight: 600;
  }
  .slug-link {
    color: $primary;
    text-decoration: none;
    font-size: 12px;
  }
  .stage-compact {
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    cursor: default;
  }
  .muted {
    color: #9e9e9e;
  }
  .mono {
    font-family: monospace;
    font-size: 12px;
  }
  .q-td.editable {
    cursor: pointer;
    .edit-hint {
      opacity: 0;
      margin-left: 4px;
      color: $primary;
      transition: opacity 0.15s;
    }
    &:hover .edit-hint {
      opacity: 0.7;
    }
  }

  // Keep every header on a single line so they all share one baseline (the long
  // French labels otherwise wrap to two lines and stagger against the short ones).
  // The table scrolls horizontally, so single-line headers are fine.
  .q-table thead th {
    white-space: nowrap;
    vertical-align: bottom !important;
  }
}
.feature-tooltip {
  text-transform: capitalize;
}
</style>
